import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { getPatientById } from 'api/account/getPatient';
import { Patient } from 'api/account/patients.type';
import { TreatPharmacy } from 'api/pharmacy/TreatPharmacy.types';
import { RadioButton } from 'app-pages/pages/consult/components/raw-components/RadioButton';
import { preferredName } from 'components/_temp_ui_common/utilities/preferredName';
import { preventDefault } from 'components/_temp_ui_common/utilities/preventDefault';
import CallForHelpWinkBox from 'components/call-for-help/CallForHelpWinkBox';
import { CalloutBox, CalloutHeader } from 'components/core/callout/Callout';
import {
  customizeValidatorMessage,
  isPostalCode,
  required,
  strLenMin,
  subtleRequired,
  useForm,
} from 'components/core/form';
import { AppLink } from 'components/core/link/Link';
import { H5, Text } from 'components/core/text/Text';
import { theme } from 'components/core/theme';
import { Input } from 'components/form/Input';
import { Select } from 'components/form/Select';
import { Modal, ModalBody } from 'components/modal/Modal';
import { FeatureFlags, useFlag } from 'hooks/useFlag';
import { X } from 'react-feather';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import {
  firstLetterIsCharacter,
  lastLetterIsCharacter,
  over1,
  under18,
} from 'utils/formValidations';
import USStates from 'utils/US-States';

import { CareIsFor } from '../account-setup.types';
import {
  ethnicityOptions,
  FormProps,
  genderOptions,
  pronounOptions,
  Props,
  sexOptions,
} from './FormConsts';
import PharmacySearch from './PharmacySearch';

const StateOptions = USStates.map(s => ({
  label: s.name,
  value: s.name,
}));

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  gap: 20px;
  padding-bottom: 64px;

  @media (max-width: 768px) {
    padding-bottom: 48px;
  }

  > h5 {
    margin-top: 20px;
  }
`;

const UpdateChildForm: FC<Props> = ({
  patientId,
  careIsFor,
  onPatientUpdate,
  hideHospitalizationInfo,
}) => {
  const { registerField } = useForm<FormProps>();
  const t = useTranslations('UpdateChildTranslations');
  const isAdultEnrollmentEnabled = useFlag(
    FeatureFlags.AdultMembersEnabled,
    true,
  );
  const collectHospitalizationInfo = useFlag(
    FeatureFlags.CollectHospitalizationInfo,
    true,
  );

  const firstNameRef = useRef('');
  const preferredNameRef = useRef('');
  const [displayName, setDisplayName] = useState('');

  const query = useParams();
  const isUpdatingPatient = !!query['patientId'];

  const isSelf = CareIsFor.Self === careIsFor;

  const [watchedZip, setWatchedZip] = useState<string>('');
  const [patient, setPatient] = useState<Patient>();
  const [addNickname, setAddNickName] = useState<boolean>(false);
  const [addPronouns, setAddPronouns] = useState<boolean>(false);
  const [isHoverNickName, setIsHoverNickName] = useState(false);
  const [isHoverPronouns, setIsHoverPronouns] = useState(false);
  const [hospitalizationAgreed, setHospitalizationAgreed] = useState<boolean | null>(null);
  const [showHospitalizationModal, setShowHospitalizationModal] =
    useState(false);

  const under18Validator = useMemo(() => {
    if (!isAdultEnrollmentEnabled) return under18;

    return customizeValidatorMessage(
      under18,
      `Looks like ${
        (displayName && `${displayName} is`) || 'they are'
      } an adult. Adults must enroll directly with Bend.`,
    );
  }, [isAdultEnrollmentEnabled, displayName]);

  const onNameBlur = (position: 'first' | 'preferred', value: string) => {
    if (position === 'first') firstNameRef.current = value;
    else preferredNameRef.current = value;

    setDisplayName(preferredNameRef.current || firstNameRef.current);
  };

  useEffect(() => {
    if (!patientId) return;

    const fetchData = async () => {
      const patient = await getPatientById(patientId);
      setPatient(patient);
      if (patient) {
        if (onPatientUpdate) onPatientUpdate(patient);
        if (patient.nickname) setAddNickName(true);
        if (patient.pronouns) setAddPronouns(true);
        setDisplayName(preferredName(patient));
      }
    };

    fetchData();
  }, [onPatientUpdate, patientId]);

  const handlePharmacyUpdate = (_pharm: TreatPharmacy | null) => {
    // no-op
  };

  const showAddNickName = (e: MouseEvent) => {
    e.preventDefault();
    setIsHoverNickName(false);
    setAddNickName(!addNickname);
  };

  const showAddPronouns = (e: MouseEvent) => {
    e.preventDefault();
    setIsHoverPronouns(false);
    setAddPronouns(!addPronouns);
  };

  const handleMouseEnterPronouns = () => {
    setIsHoverPronouns(true);
  };

  const handleMouseLeavePronouns = () => {
    setIsHoverPronouns(false);
  };

  const handleMouseEnterNickName = () => {
    setIsHoverNickName(true);
  };

  const handleMouseLeaveNickName = () => {
    setIsHoverNickName(false);
  };

  return (
    <Wrapper>
      {!isSelf && (
        <Input
          type={'text'}
          label={t('firstName')}
          defaultValue={patient?.firstName ?? undefined}
          data-testid="child-form-firstName"
          {...registerField('firstName', {
            validators: [
              subtleRequired('Please tell us their first name'),
              firstLetterIsCharacter,
              strLenMin(2),
            ],
          })}
          placeholder={t('firstName')}
          autoComplete="given-name"
          onBlur={e => onNameBlur('first', e.target.value)}
        />
      )}
      {!isSelf && (
        <Input
          type={'text'}
          label={t('lastName')}
          defaultValue={patient?.lastName ?? undefined}
          data-testid="child-form-lastName"
          {...registerField('lastName', {
            validators: [
              subtleRequired('Please tell us their last name'),
              lastLetterIsCharacter,
              strLenMin(2),
            ],
          })}
          placeholder={t('lastName')}
          autoComplete="family-name"
        />
      )}

      {!isSelf && !addNickname ? (
        <div>
          <AppLink
            to=""
            className="mb-6S"
            onClick={e => {
              e.preventDefault();
              showAddNickName(e);
            }}
          >
            {t('addNickname')}
          </AppLink>
        </div>
      ) : (
        <></>
      )}

      {!isSelf && addNickname ? (
        <div className="flex flex-row relative">
          <Input
            type={'text'}
            label={t('nickName')}
            defaultValue={patient?.nickname ?? undefined}
            data-testid="child-form-nickname"
            {...registerField('nickname')}
            placeholder={t('nickName')}
            onBlur={e => onNameBlur('preferred', e.target.value)}
          />
          <div
            className="cursor-pointer"
            style={{
              position: 'absolute',
              right: '10px',
              top: '10px',
              width: '40px',
              height: '40px',
              backgroundColor: isHoverNickName ? '#e2e8f0' : '',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={e => showAddNickName(e)}
            onMouseEnter={handleMouseEnterNickName}
            onMouseLeave={handleMouseLeaveNickName}
          >
            <X />
          </div>
        </div>
      ) : (
        <></>
      )}

      {!isSelf && (
        <Input
          id="birth-date"
          type={'date'}
          label={t('birthDate')}
          data-testid="child-form-birthDate"
          {...registerField('birthDate', {
            validators: [
              subtleRequired('Please tell us their date of birth'),
              over1,
              under18Validator,
            ],
          })}
          defaultValue={patient?.dateOfBirth}
        />
      )}
      <Select
        label={t('sex')}
        data-testid="child-form-sex"
        id="sex"
        placeholder="Select"
        {...registerField('sex', {
          validateOnBlur: false,
          validators: [subtleRequired('Please add their sex')],
        })}
        options={sexOptions}
        key={`sex:${patient?.sexAtBirth}`}
        defaultValue={
          patient?.sexAtBirth
            ? sexOptions.find(o => o.value === patient?.sexAtBirth)
            : undefined
        }
      />
      <Select
        label={t('gender')}
        data-testid="child-form-gender"
        id="gender"
        placeholder="Select"
        {...registerField('gender', {
          validateOnBlur: false,
          validators: [subtleRequired('Please add their gender')],
        })}
        defaultValue={
          patient?.gender
            ? genderOptions.find(o => o.value === patient?.gender)
            : undefined
        }
        key={`gender:${patient?.gender}`}
        options={genderOptions}
      />

      {!addPronouns ? (
        <div className="mb-5">
          <AppLink
            to=""
            onClick={e => {
              e.preventDefault();
              showAddPronouns(e);
            }}
          >
            {t('addPreferredPronouns')}
          </AppLink>
        </div>
      ) : (
        <></>
      )}

      {addPronouns ? (
        <div className="flex flex-row relative">
          <Select
            label={t('pronouns')}
            data-testid="child-form-pronouns"
            id="pronouns"
            placeholder="Select"
            {...registerField('pronouns', {
              validateOnBlur: false,
              validators: [
                subtleRequired('Please tell us their preferred pronouns'),
              ],
            })}
            defaultValue={
              patient?.pronouns
                ? pronounOptions.filter(
                    o => patient?.pronouns.indexOf(o.value) > -1,
                  )
                : undefined
            }
            options={pronounOptions}
          />

          <div
            className="cursor-pointer"
            style={{
              position: 'absolute',
              right: '35px',
              top: '10px',
              width: '40px',
              height: '40px',
              backgroundColor: isHoverPronouns ? '#e2e8f0' : '',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={e => showAddPronouns(e)}
            onMouseEnter={handleMouseEnterPronouns}
            onMouseLeave={handleMouseLeavePronouns}
          >
            <X />
          </div>
        </div>
      ) : (
        <></>
      )}

      {isSelf && !addNickname ? (
        <div>
          <AppLink
            to=""
            className="mb-6S"
            onClick={e => {
              e.preventDefault();
              showAddNickName(e);
            }}
          >
            {t('addNickname')}
          </AppLink>
        </div>
      ) : (
        <></>
      )}
      {isSelf && addNickname ? (
        <div className="flex flex-row relative">
          <Input
            type={'text'}
            label={t('nickName')}
            defaultValue={patient?.nickname ?? undefined}
            data-testid="child-form-nickname"
            {...registerField('nickname')}
            placeholder={t('nickName')}
          />
          <div
            className="cursor-pointer"
            style={{
              position: 'absolute',
              right: '10px',
              top: '10px',
              width: '40px',
              height: '40px',
              backgroundColor: isHoverNickName ? '#e2e8f0' : '',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={e => showAddNickName(e)}
            onMouseEnter={handleMouseEnterNickName}
            onMouseLeave={handleMouseLeaveNickName}
          >
            <X />
          </div>
        </div>
      ) : (
        <></>
      )}

      <Select
        label={t('raceEthnicity')}
        data-testid="child-form-raceEthnicity"
        id="raceEthnicity"
        placeholder="Select"
        styles={{
          multiValue: provided => ({ ...provided, marginTop: '25px' }),
        }}
        {...registerField('raceEthnicity', {
          validateOnBlur: false,
          validators: [subtleRequired('Please add their race / ethnicity')],
          dataType: 'pipeArray',
        })}
        isMulti
        delimiter="|"
        options={ethnicityOptions}
        key={patient?.ethnicity}
        defaultValue={
          patient?.ethnicity
            ? ethnicityOptions.filter(
                o => patient?.ethnicity.indexOf(o.value) > -1,
              )
            : undefined
        }
      />

      {!hideHospitalizationInfo && collectHospitalizationInfo && (
        <div>
          {showHospitalizationModal && (
            <Modal onClose={() => setShowHospitalizationModal(false)}>
              <ModalBody>
                <Text variant="text5">
                  A hospitalization in the past 60 days is important information
                  and will help Bend Health get you the right care as fast as
                  possible.
                </Text>
              </ModalBody>
            </Modal>
          )}

          <p style={{ marginBottom: '16px' }}>
            <Text variant="text5">
              Has this person been in in-patient, residential, partial
              hospitalization, intensive outpatient or the ER in the last 60
              days?{' '}
              <AppLink
                to=""
                className="mb-6S"
                onClick={e => {
                  e.preventDefault();
                  setShowHospitalizationModal(true);
                }}
              >
                Why does this matter?
              </AppLink>
            </Text>
          </p>

          <p style={{ marginBottom: '16px' }} className="flex">
            <RadioButton
              data-testid="no-hospitalization"
              href="#link1"
              role="tablist"
              onClick={preventDefault(
                () => setHospitalizationAgreed(false),
                true,
              )}
              style={{
                borderColor: '#d0d0d3',
                backgroundColor:
                  hospitalizationAgreed === false
                    ? theme.colors.global.yellowMedium
                    : '#fff',
                color: theme.colors.global.greyDark,
              }}
            >
              No
            </RadioButton>
            <RadioButton
              data-testid="yes-hospitalization"
              href="#link1"
              role="tablist"
              onClick={preventDefault(
                () => setHospitalizationAgreed(true),
                true,
              )}
              style={{
                borderColor: '#d0d0d3',
                borderLeft: '1px solid #d0d0d3',
                backgroundColor: hospitalizationAgreed === true
                  ? theme.colors.global.yellowMedium
                  : '#fff',
                color: theme.colors.global.greyDark,
              }}
            >
              Yes
            </RadioButton>
          </p>

          {hospitalizationAgreed && (
            <p>
              <p style={{ marginBottom: '16px' }}>
                <Text variant="text5">
                  Please provide the following information:
                </Text>
              </p>
              <Input
                type={'text'}
                label={'Name of Facility'}
                placeholder={'Name of Facility'}
                {...registerField('hospitalizationFacility', {
                  validators: hospitalizationAgreed
                    ? [
                        subtleRequired(
                          'Please tell us their hospitalization facility',
                        ),
                        firstLetterIsCharacter,
                        strLenMin(2),
                      ]
                    : [],
                })}
              />
            </p>
          )}
        </div>
      )}

      <CallForHelpWinkBox />

      <H5>{t('address')}</H5>
      <Input
        type={'text'}
        label={t('street1')}
        data-testid="child-form-street1"
        {...registerField('street1', {
          validators: [subtleRequired('Street 1 is required'), strLenMin(1)],
        })}
        placeholder={t('street1')}
        defaultValue={patient?.streetAddress1}
        autoComplete="address-line1"
      />
      <Input
        type={'text'}
        label={t('street2')}
        data-testid="child-form-street2"
        {...registerField('street2')}
        placeholder={t('street2')}
        defaultValue={patient?.streetAddress2}
        autoComplete="address-line2"
      />
      <div className="flex flex-row">
        <Input
          type={'text'}
          label={t('city')}
          style={{ width: '90%' }}
          data-testid="child-form-city"
          {...registerField('city', {
            validators: [subtleRequired('City is required'), strLenMin(1)],
          })}
          placeholder={t('city')}
          defaultValue={patient?.city}
          autoComplete="home city"
        />
        <Select
          className="w-full"
          label={t('state')}
          data-testid="child-form-usState"
          id="state"
          placeholder="Select"
          {...registerField('state', {
            validateOnBlur: false,
            validators: [subtleRequired('State is required'), required],
          })}
          options={StateOptions}
          key={patient?.state}
          defaultValue={
            patient?.state
              ? StateOptions.find(o => o.value === patient?.state)
              : undefined
          }
        />
      </div>
      <Input
        label={t('zipCode')}
        data-testid="child-form-zipCode"
        {...registerField('zipCode', {
          validators: [subtleRequired('Zip Code is required'), isPostalCode],
        })}
        placeholder={t('zipCode')}
        onChange={e => setWatchedZip(e.target.value)}
        defaultValue={patient?.zipCode}
        autoComplete="postal-code"
      />

      <H5>{t('pharmacy')}</H5>
      <PharmacySearch
        zip={watchedZip || patient?.zipCode}
        registerField={registerField}
        onChange={pharm => handlePharmacyUpdate(pharm)}
        pharmacyId={patient?.ayvaPharmacyUid?.toString()}
      />
      {!isUpdatingPatient && !isSelf && (
        <CalloutBox className="mt-5">
          <p>
            <CalloutHeader>Need to add another child or teen?</CalloutHeader>
          </p>
          <Text variant="text5" block as="p">
            You can do so later from within your account settings.
          </Text>
        </CalloutBox>
      )}
    </Wrapper>
  );
};

export default UpdateChildForm;
